import {
  BLOCK_TYPES,
  type Block,
  BlockSchema,
  type BlockType,
  PageMetaSchema,
  PageSchema,
  PostMetaSchema,
} from "@aurumiq/cms-schema";
import type { z } from "zod";
import { describe, emptyObject, type FieldSpec } from "../schema-form/introspect";

export const BLOCK_LABELS: Record<BlockType, string> = {
  hero: "Hero",
  stats: "Stats",
  steps: "How it works",
  features: "Features",
  screenshots: "Screenshots",
  video: "Video",
  pricing: "Pricing",
  faq: "FAQ",
  cta: "Call to action",
  logos: "Logos",
  testimonial: "Testimonials",
  richtext: "Rich text",
  freeform: "Free layout",
};

/** Per-type zod object schema (from the discriminated union). */
export const BLOCK_SCHEMAS = Object.fromEntries(
  BlockSchema.options.map((o) => [o.shape.type.value, o]),
) as unknown as Record<BlockType, z.ZodType>;

/** Introspected field specs per block type (memoised at module load). */
export const BLOCK_FIELDS: Record<BlockType, Record<string, FieldSpec>> = Object.fromEntries(
  BLOCK_TYPES.map((t) => {
    const spec = describe(BLOCK_SCHEMAS[t]);
    return [t, spec.kind === "object" ? spec.fields : {}];
  }),
) as Record<BlockType, Record<string, FieldSpec>>;

/** SEO / page meta fields, minus the post block (rendered by its own panel). */
export const META_FIELDS: Record<string, FieldSpec> = (() => {
  const spec = describe(PageMetaSchema);
  if (spec.kind !== "object") return {};
  const { post: _post, ...rest } = spec.fields;
  return rest;
})();

export const POST_FIELDS: Record<string, FieldSpec> = (() => {
  const spec = describe(PostMetaSchema);
  return spec.kind === "object" ? spec.fields : {};
})();

/** Keys shared by every block; rendered by the editor chrome, not the form. */
export const BLOCK_BASE_KEYS = ["id", "type", "hidden", "anchor"] as const;

export function isBlockType(t: unknown): t is BlockType {
  return typeof t === "string" && (BLOCK_TYPES as readonly string[]).includes(t);
}

export function newBlockId(type: string): string {
  return `${type}-${Math.random().toString(36).slice(2, 8)}`;
}

/** A fresh block with every required field present but empty. */
export function newBlock(type: BlockType): Record<string, unknown> {
  return { ...emptyObject(BLOCK_FIELDS[type]), id: newBlockId(type), type, hidden: false };
}

/** Editor row: the raw JSON plus a stable client key (blocks from the API may lack ids). */
export interface EditorBlock {
  key: string;
  raw: Record<string, unknown>;
}

export interface BlockValidation {
  ok: boolean;
  block?: Block;
  issues: z.core.$ZodIssue[];
  knownType: boolean;
}

export function validateBlock(raw: unknown): BlockValidation {
  const r = BlockSchema.safeParse(raw);
  const type =
    typeof raw === "object" && raw !== null ? (raw as { type?: unknown }).type : undefined;
  if (r.success) return { ok: true, block: r.data, issues: [], knownType: true };
  return { ok: false, issues: r.error.issues, knownType: isBlockType(type) };
}

export function toEditorBlocks(blocks: unknown[]): EditorBlock[] {
  return blocks.map((b, i) => {
    const raw = typeof b === "object" && b !== null ? (b as Record<string, unknown>) : { value: b };
    const id = typeof raw.id === "string" ? raw.id : "";
    return { key: id ? `${id}#${i}` : `blk#${i}`, raw };
  });
}

/** One-line summary for the block list (headline / heading / first item). */
export function blockSummary(raw: Record<string, unknown>): string {
  for (const k of ["headline", "heading", "html"]) {
    const v = raw[k];
    if (typeof v === "string" && v.trim()) return v.replace(/<[^>]+>/g, "").slice(0, 60);
  }
  const items = raw.items;
  if (Array.isArray(items) && items.length)
    return `${items.length} item${items.length > 1 ? "s" : ""}`;
  const codes = raw.plan_codes;
  if (Array.isArray(codes) && codes.length) return codes.join(", ");
  return "";
}

/** Page payload for the landing preview frame; validated against PageSchema. */
export function buildPreviewPage(input: {
  slug: string;
  kind: string;
  title: string;
  blocks: EditorBlock[];
  meta: Record<string, unknown>;
}) {
  const blocks = input.blocks
    .map((b) => validateBlock(b.raw))
    .filter((v) => v.ok)
    .map((v) => v.block);
  const meta = PageMetaSchema.safeParse(input.meta);
  const page = PageSchema.safeParse({
    slug: input.slug,
    kind: input.kind,
    title: input.title,
    blocks,
    meta: meta.success ? meta.data : undefined,
  });
  return page.success ? page.data : null;
}
